/**
 * Realtime client (Discord 100% — Priority 0).
 *
 * Primary transport is Server-Sent Events (`GET /api/events?channels=...`).
 * EventSource auto-reconnects, works through every reverse proxy, and only
 * needs a session cookie or `?token=` for auth — no upgrade dance.
 *
 * Subscriptions: `subscribeChannel("<server_id>:<channel>")` /
 * `subscribeThread(threadId)` / `subscribeServer(serverId)` build a topic
 * set; whenever it changes we tear down the open EventSource and open a new
 * one with the updated query string. This is cheap because the underlying
 * broadcast bus on the backend is shared.
 *
 * Typing: SSE is one-way, so typing notices go through
 * `POST /api/events/typing` instead. The bus then echoes them back to every
 * subscriber.
 *
 * Reconnect schedule: 1s, 2s, 5s, 10s, 10s capped — same shape as the
 * old WebSocket client so the UI's "reconnecting…" state stays consistent.
 */

import { API_BASE } from './api';
import { state } from './state';

export type RealtimeEvent =
  | { type: 'message_created'; channel_id: string; thread_id: string | null; message: RealtimeMessage }
  | { type: 'message_edited'; channel_id: string; thread_id: string | null; message: RealtimeMessage }
  | { type: 'message_deleted'; channel_id: string; thread_id: string | null; message_id: string }
  | { type: 'reaction_changed'; channel_id: string; message_id: string; reactions: Array<{ emoji: string; count: number }> }
  | { type: 'typing_changed'; channel_id: string; identity_id: string; typing_until: string }
  | { type: 'cell_status_updated'; channel_id: string; snapshot: Record<string, unknown> }
  | { type: 'voice_note_created'; channel_id: string; note: { id: string; from: string; transcript: string; has_audio: boolean; created_at: string } }
  | { type: 'call_incoming'; channel_id: string; call: CallRow }
  | { type: 'call_answered'; channel_id: string; call: CallRow }
  | { type: 'call_ended'; channel_id: string; call: CallRow }
  | { type: 'call_updated'; channel_id: string; call: CallRow }
  | { type: 'dm_created'; channel_id: string; dm: RealtimeDm }
  | { type: 'channel_created'; channel_id: string; channel: unknown }
  | { type: 'channel_updated'; channel_id: string; channel: unknown }
  | { type: 'channel_deleted'; channel_id: string; deleted_channel_id: string }
  | { type: 'server_created'; channel_id: string; server: RealtimeServer }
  | { type: 'server_updated'; channel_id: string; server: RealtimeServer }
  | { type: 'server_deleted'; channel_id: string; server_id: string }
  | { type: 'ping' }
  | { type: 'resync'; missed: number };

/** Server row shape matching the backend `Server` struct. */
export interface RealtimeServer {
  id: string;
  name: string;
  type: string;
  visibility: string;
  icon: string;
  description: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

/** Direct message row shape matching the backend `DirectMessage` struct. */
export interface RealtimeDm {
  id: string;
  from_id: string;
  to_id: string;
  content: string;
  read_at: string | null;
  created_at: string;
}

/** Full call row as returned by `/api/calls` — mirrors the backend `CallRow`. */
export interface CallRow {
  id: string;
  server_id: string;
  channel_id: string | null;
  direction: 'in' | 'out';
  status: 'ringing' | 'active' | 'ended' | 'missed' | 'busy' | 'no_answer' | 'failed' | 'rejected';
  counterparty_phone: string;
  counterparty_person_id: string | null;
  counterparty_name: string | null;
  initiator_id: string | null;
  livekit_room: string | null;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  duration_s: number | null;
  end_reason: string | null;
  created_at: string;
}

export interface RealtimeMessage {
  id: string;
  server_id: string;
  channel: string;
  author_id: string;
  content: string;
  edited_at: string | null;
  reply_to: string | null;
  attachments: string;
  reactions: string;
  pinned: number;
  created_at: string;
  encrypted_with_key_id?: string | null;
}

type Listener = (ev: RealtimeEvent) => void;
type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';
type ConnectionListener = (s: ConnectionState) => void;

const BACKOFF_SCHEDULE_MS = [1000, 2000, 5000, 10000];
/** When subscriptions change, debounce stream rebuilds by this much. */
const REBUILD_DEBOUNCE_MS = 50;

class RealtimeClient {
  private es: EventSource | null = null;
  private listeners = new Set<Listener>();
  private connListeners = new Set<ConnectionListener>();
  private subscribedChannels = new Set<string>(); // "<server_id>:<channel>"
  private subscribedThreads = new Set<string>();
  private subscribedServers = new Set<string>();
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private rebuildTimer: ReturnType<typeof setTimeout> | null = null;
  private explicitlyClosed = false;
  private state: ConnectionState = 'closed';

  /** Idempotent — calling connect() multiple times is safe. */
  connect(): void {
    if (state.flags['ws_realtime'] === false) return; // shared kill-switch
    this.explicitlyClosed = false;
    this.scheduleRebuild();
  }

  close(): void {
    this.explicitlyClosed = true;
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
    if (this.rebuildTimer) { clearTimeout(this.rebuildTimer); this.rebuildTimer = null; }
    if (this.es) { this.es.close(); this.es = null; }
    this.setState('closed');
  }

  on(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onConnectionChange(fn: ConnectionListener): () => void {
    this.connListeners.add(fn);
    fn(this.state);
    return () => this.connListeners.delete(fn);
  }

  subscribeChannel(topic: string): void {
    if (this.subscribedChannels.has(topic)) return;
    this.subscribedChannels.add(topic);
    this.scheduleRebuild();
  }

  unsubscribeChannel(topic: string): void {
    if (!this.subscribedChannels.has(topic)) return;
    this.subscribedChannels.delete(topic);
    this.scheduleRebuild();
  }

  subscribeThread(threadId: string): void {
    if (this.subscribedThreads.has(threadId)) return;
    this.subscribedThreads.add(threadId);
    this.scheduleRebuild();
  }

  unsubscribeThread(threadId: string): void {
    if (!this.subscribedThreads.has(threadId)) return;
    this.subscribedThreads.delete(threadId);
    this.scheduleRebuild();
  }

  /** Subscribe to every event under `<server_id>:*`. Useful for unread badges. */
  subscribeServer(serverId: string): void {
    if (this.subscribedServers.has(serverId)) return;
    this.subscribedServers.add(serverId);
    this.scheduleRebuild();
  }

  unsubscribeServer(serverId: string): void {
    if (!this.subscribedServers.has(serverId)) return;
    this.subscribedServers.delete(serverId);
    this.scheduleRebuild();
  }

  /** Send a typing notice — REST instead of WS because SSE is one-way. */
  sendTyping(channelTopic: string, identityId: string): void {
    if (this.explicitlyClosed) return;
    void fetch(`${API_BASE}/api/events/typing`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ channel_id: channelTopic, identity_id: identityId }),
    }).catch(() => { /* silent — typing is best-effort */ });
  }

  getState(): ConnectionState { return this.state; }

  // ── internals ────────────────────────────────────────────────────────────

  /**
   * Rebuild the EventSource on the next tick. EventSource is one-way, so
   * any subscription change requires a fresh URL. We debounce so that a
   * burst of `subscribeChannel/subscribeServer` calls during channel-switch
   * collapses to a single connect.
   */
  private scheduleRebuild(): void {
    if (this.explicitlyClosed) return;
    if (this.rebuildTimer) return;
    this.rebuildTimer = setTimeout(() => {
      this.rebuildTimer = null;
      this.openStream();
    }, REBUILD_DEBOUNCE_MS);
  }

  private openStream(): void {
    if (this.explicitlyClosed) return;
    if (state.flags['ws_realtime'] === false) return;

    // Nothing to subscribe to — keep the stream closed (no point holding
    // an idle SSE connection open).
    if (this.subscribedChannels.size === 0
        && this.subscribedThreads.size === 0
        && this.subscribedServers.size === 0) {
      if (this.es) { this.es.close(); this.es = null; }
      this.setState('closed');
      return;
    }

    if (this.es) { this.es.close(); this.es = null; }
    this.setState(this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting');

    const url = this.buildUrl();
    const es = new EventSource(url, { withCredentials: true });
    this.es = es;
    this.attachHandlers(es);
  }

  private buildUrl(): string {
    const params = new URLSearchParams();
    if (this.subscribedChannels.size > 0) {
      params.set('channels', Array.from(this.subscribedChannels).join(','));
    }
    if (this.subscribedThreads.size > 0) {
      params.set('threads', Array.from(this.subscribedThreads).join(','));
    }
    // Only one server at a time is currently rendered (channel-list scope),
    // but the API takes the most recent one — pick deterministically.
    if (this.subscribedServers.size > 0) {
      params.set('server_id', Array.from(this.subscribedServers)[0] || '');
    }
    // Cache-buster so intermediaries can't accidentally collapse two opens.
    params.set('_', String(Date.now()));
    return `${API_BASE}/api/events?${params.toString()}`;
  }

  private attachHandlers(es: EventSource): void {
    es.addEventListener('open', () => {
      this.reconnectAttempt = 0;
      this.setState('open');
    });

    es.addEventListener('ready', () => {
      // Server-confirmed open — already covered by 'open', but if the EventSource
      // for whatever reason hasn't fired 'open' yet (proxy buffering quirks),
      // the first SSE frame will trip this listener and we can flip state.
      if (this.state !== 'open') this.setState('open');
    });

    // Named events from the server (one per RealtimeEvent variant).
    const dispatchNamed = (e: MessageEvent, type: RealtimeEvent['type']): void => {
      try {
        const parsed = JSON.parse(e.data) as Record<string, unknown>;
        // The backend's serde tag lives inside `parsed` already; it sets
        // {"type":"message_created", ...} for MessageCreated etc. We trust
        // the SSE event name as a tiebreaker but use the JSON for the type.
        const ev = (parsed.type ? parsed : { ...parsed, type }) as unknown as RealtimeEvent;
        this.dispatch(ev);
      } catch {
        /* ignore — SSE frames must be JSON */
      }
    };

    es.addEventListener('message_created', (e) => dispatchNamed(e as MessageEvent, 'message_created'));
    es.addEventListener('message_edited', (e) => dispatchNamed(e as MessageEvent, 'message_edited'));
    es.addEventListener('message_deleted', (e) => dispatchNamed(e as MessageEvent, 'message_deleted'));
    es.addEventListener('reaction_changed', (e) => dispatchNamed(e as MessageEvent, 'reaction_changed'));
    es.addEventListener('typing_changed', (e) => dispatchNamed(e as MessageEvent, 'typing_changed'));
    es.addEventListener('cell_status_updated', (e) => dispatchNamed(e as MessageEvent, 'cell_status_updated'));
    es.addEventListener('voice_note_created', (e) => dispatchNamed(e as MessageEvent, 'voice_note_created'));
    es.addEventListener('call_incoming', (e) => dispatchNamed(e as MessageEvent, 'call_incoming'));
    es.addEventListener('call_answered', (e) => dispatchNamed(e as MessageEvent, 'call_answered'));
    es.addEventListener('call_ended', (e) => dispatchNamed(e as MessageEvent, 'call_ended'));
    es.addEventListener('call_updated', (e) => dispatchNamed(e as MessageEvent, 'call_updated'));
    es.addEventListener('dm_created', (e) => dispatchNamed(e as MessageEvent, 'dm_created'));
    es.addEventListener('channel_created', (e) => dispatchNamed(e as MessageEvent, 'channel_created'));
    es.addEventListener('channel_updated', (e) => dispatchNamed(e as MessageEvent, 'channel_updated'));
    es.addEventListener('channel_deleted', (e) => dispatchNamed(e as MessageEvent, 'channel_deleted'));
    es.addEventListener('server_created', (e) => dispatchNamed(e as MessageEvent, 'server_created'));
    es.addEventListener('server_updated', (e) => dispatchNamed(e as MessageEvent, 'server_updated'));
    es.addEventListener('server_deleted', (e) => dispatchNamed(e as MessageEvent, 'server_deleted'));

    es.addEventListener('resync', (e) => {
      try {
        const parsed = JSON.parse((e as MessageEvent).data) as { missed?: number };
        this.dispatch({ type: 'resync', missed: parsed.missed ?? 0 });
      } catch { /* ignore */ }
    });

    // The default 'message' event fires for unnamed frames — nothing on our
    // server emits one, so this is a defensive no-op.
    es.addEventListener('message', () => { /* ignore */ });

    es.addEventListener('error', () => {
      // Browsers fire `error` for both transient blips (auto-reconnect inside
      // EventSource) and hard failures. Treat readyState=CLOSED as needing
      // a manual reconnect so we control the backoff.
      if (es.readyState === EventSource.CLOSED) {
        this.es = null;
        if (!this.explicitlyClosed) this.scheduleReconnect();
      } else {
        this.setState('reconnecting');
      }
    });
  }

  private dispatch(ev: RealtimeEvent): void {
    for (const fn of this.listeners) {
      try { fn(ev); } catch (e) { console.error('realtime listener error', e); }
    }
  }

  private scheduleReconnect(): void {
    if (this.explicitlyClosed) return;
    if (this.reconnectTimer) return;
    const delay = BACKOFF_SCHEDULE_MS[Math.min(this.reconnectAttempt, BACKOFF_SCHEDULE_MS.length - 1)] ?? 10000;
    this.reconnectAttempt += 1;
    this.setState('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openStream();
    }, delay);
  }

  private setState(s: ConnectionState): void {
    if (this.state === s) return;
    this.state = s;
    for (const fn of this.connListeners) {
      try { fn(s); } catch { /* ignore */ }
    }
  }
}

export const realtime = new RealtimeClient();

/** Compose the channel topic key the server uses (`<server_id>:<channel>`). */
export function channelTopic(serverId: string, channel: string): string {
  return `${serverId}:${channel}`;
}

/** DM inbox topic — one per person, fans out every DM they send or receive. */
export function dmInboxTopic(personId: string): string {
  return `dm-inbox:${personId}`;
}
